import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionLabel from "../ui/SectionLabel";
import SplitHeading from "../ui/SplitHeading";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";
import SectionSubtitle from "../ui/SectionSubtitle";

function FAQItem({ item, num, isOpen, onToggle }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: "relative", borderBottom: "1px solid rgba(242,232,216,0.06)", overflow: "hidden" }}
    >
      <div aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, width: 2, height: "100%", zIndex: 1 }}>
        <motion.div
          animate={{ scaleY: isOpen ? 1 : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: "100%", height: "100%", background: "#7CC4D8", transformOrigin: "top", boxShadow: "0 0 10px rgba(124,196,216,0.35)" }}
        />
      </div>

      <div
        aria-hidden="true"
        style={{
          position: "absolute", right: "-0.5rem", top: "50%", transform: "translateY(-50%)",
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: "clamp(4rem, 8vw, 7rem)", fontWeight: 700,
          color: isOpen ? "rgba(124,196,216,0.06)" : "rgba(242,232,216,0.03)",
          lineHeight: 1, pointerEvents: "none", userSelect: "none", transition: "color 0.5s ease",
        }}
      >
        {num}
      </div>

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full text-left"
        style={{ position: "relative", zIndex: 2, padding: "2rem 0 2rem 2rem", background: "transparent", border: "none", outline: "none", display: "flex", alignItems: "center", gap: "1.5rem" }}
      >
        <span
          className="font-mono text-[10px] tracking-[0.2em] shrink-0"
          style={{ color: isOpen ? "#7CC4D8" : "rgba(124,196,216,0.35)", transition: "color 0.35s ease" }}
        >
          {num}
        </span>

        <span
          className="font-body text-base flex-1"
          style={{
            color: isOpen || hovered ? "#F2E8D8" : "rgba(242,232,216,0.75)",
            transform: isOpen || hovered ? "translateX(6px)" : "translateX(0)",
            transition: "color 0.3s ease, transform 0.35s cubic-bezier(0.22,1,0.36,1)",
            display: "block", fontWeight: isOpen ? 500 : 400,
          }}
        >
          {item.q}
        </span>

        <div style={{ width: 18, height: 18, position: "relative", flexShrink: 0, marginRight: "0.5rem" }}>
          <div style={{ position: "absolute", top: "50%", left: 0, width: "100%", height: 1, background: "rgba(124,196,216,0.45)", transform: "translateY(-50%)" }} />
          <motion.div
            animate={{ scaleY: isOpen ? 0 : 1 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 1, height: "100%", background: "rgba(124,196,216,0.45)", transformOrigin: "center" }}
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden", position: "relative", zIndex: 2 }}
          >
            <p className="font-body text-sm leading-[1.85]" style={{ color: "rgba(242,232,216,0.55)", padding: "0 1rem 2rem 3.5rem", maxWidth: "68ch" }}>
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const { lang } = useLang();
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex((prev) => (prev === i ? null : i));

  const faqs = c.faq.items.map((item) => ({ q: item.q[lang], a: item.a[lang] }));

  return (
    <section
      id="faq"
      className="section-pad relative overflow-hidden section-depth"
      style={{ background: "#100F0C" }}
      data-testid="faq-section"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,196,216,0.03), transparent 65%)", filter: "blur(80px)" }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 right-0 w-[400px] h-[400px] rounded-full" style={{ background: "radial-gradient(circle, rgba(200,121,65,0.04), transparent 65%)", filter: "blur(80px)" }} />

      <div className="max-w-7xl mx-auto relative">
        <div className="reveal mb-14 lg:mb-18 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <SectionLabel>{c.faq.label[lang]}</SectionLabel>
            <SplitHeading
              line1={c.faq.heading1[lang]}
              line2={<em className="italic text-amber">{c.faq.ice[lang]}</em>}
              className="font-display font-light leading-[1.04] mt-5 text-white-warm"
              style={{ fontSize: "clamp(2.4rem, 4.8vw, 4rem)" }}
              data-testid="faq-title"
            />
          </div>
          <SectionSubtitle className="text-white-dim max-w-xs">
            {c.faq.subtitle[lang]}
          </SectionSubtitle>
        </div>

        <div className="reveal max-w-4xl">
          {faqs.map((item, i) => (
            <FAQItem
              key={i}
              item={item}
              num={String(i + 1).padStart(2, "0")}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>

        <div className="reveal mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <p className="font-body text-sm" style={{ color: "rgba(242,232,216,0.45)" }}>
            {c.faq.cta_question[lang]}
          </p>
          <a href="#kontakt" className="font-body text-sm flex items-center gap-2 group" style={{ color: "#C87941" }}>
            <span className="link-amber">{c.faq.cta_link[lang]}</span>
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5" style={{ color: "#C87941" }}>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
